import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, goToCheckout, fmtDate, checkoutOriginUrl } from "../api";
import { isNative } from "../mobileAuth";
import { shareLink } from "../mobileFiles";
import { useAuth } from "../AuthContext";
import { Page, SectionTitle, Logo } from "../components/Layout";
import { Share2, Instagram, Calendar, ImagePlus, Phone, MessageCircle } from "lucide-react";

const inp = "w-full border border-gray-200 rounded-xl px-4 py-3 mt-1";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABEL = { open_space: "Open Space", cabina: "Cabina Privacy" };

export const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export const socialUrl = (s) => {
  if (!s) return "";
  s = s.trim();
  if (s.startsWith("http")) return s;
  if (s.startsWith("@")) return `https://instagram.com/${s.slice(1)}`;
  if (s.includes(".")) return `https://${s}`;
  return `https://instagram.com/${s}`;
};

// ---- Image picker (locandina) ----
function ImagePicker({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Usa PNG, JPG o WEBP");
    if (file.size > 3 * 1024 * 1024) return toast.error("Immagine troppo grande (max 3 MB)");
    setBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const { data } = await api.post("/uploads/image", { data: dataUrl });
      onChange(data.url);
      toast.success("Locandina caricata");
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="block text-sm text-gray-600 mb-1">Locandina</label>
      <div className="flex items-center gap-3">
        <label className="btn-outline text-sm cursor-pointer inline-flex items-center gap-2" data-testid="flash-image-pick">
          <ImagePlus size={16} /> {busy ? "Carico…" : "Carica immagine"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} />
        </label>
        {value && <img src={imgSrc(value)} alt="locandina" className="w-16 h-16 object-cover rounded-lg border" />}
      </div>
    </div>
  );
}

const emptyForm = { booking_id: "", title: "", start_time: "18:00", end_time: "20:00", description: "", price: "", social: "", phone: "", image: "" };

function FlashForm({ initial, bookings, selectedBooking, onSubmit, submitLabel, note }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const bk = selectedBooking || bookings.find((b) => b.booking_id === f.booking_id);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      {note && <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="flash-admin-note">{note}</div>}
      {bookings && (
        <label className="block text-sm">Scegli la giornata MAS
          <select data-testid="flash-booking-select" required className={inp} value={f.booking_id} onChange={set("booking_id")}>
            <option value="">— Seleziona una tua giornata —</option>
            {bookings.map((b) => <option key={b.booking_id} value={b.booking_id}>{b.label}</option>)}
          </select>
        </label>
      )}
      {bk && <div className="text-xs text-gray-500 flex items-center gap-1"><Calendar size={13} /> Data: <b>{bk.date}</b> · {TYPE_LABEL[bk.station_type]}</div>}
      <label className="block text-sm">Titolo<input data-testid="flash-title" required className={inp} value={f.title} onChange={set("title")} /></label>
      <div className="flex gap-2">
        <label className="block text-sm flex-1">Inizio<input data-testid="flash-start" type="time" required className={inp} value={f.start_time} onChange={set("start_time")} /></label>
        <label className="block text-sm flex-1">Fine<input data-testid="flash-end" type="time" className={inp} value={f.end_time} onChange={set("end_time")} /></label>
      </div>
      <label className="block text-sm">Descrizione breve<textarea data-testid="flash-desc" required className={inp} value={f.description} onChange={set("description")} /></label>
      <label className="block text-sm">Prezzo o fascia (facoltativo)<input data-testid="flash-price" className={inp} value={f.price} onChange={set("price")} placeholder="es. da 80 €" /></label>
      <label className="block text-sm">Social / Instagram<input data-testid="flash-social" className={inp} value={f.social} onChange={set("social")} placeholder="@iltuoprofilo" /></label>
      <label className="block text-sm">Telefono / WhatsApp<input data-testid="flash-phone" className={inp} value={f.phone} onChange={set("phone")} placeholder="es. +39 333 1234567" /></label>
      <ImagePicker value={f.image} onChange={(url) => setF({ ...f, image: url })} />

      {/* Anteprima */}
      <div className="card p-4 mt-2" data-testid="flash-preview">
        <div className="text-[0.6rem] tracking-widest uppercase text-gray-400 text-center">Anteprima</div>
        {f.image && <img src={imgSrc(f.image)} alt="" className="w-full rounded-xl mt-2 max-h-56 object-cover" />}
        <div className="serif text-xl mt-3 text-center">{f.title || "Titolo Flash Day"}</div>
        {bk && <div className="text-violet-700 font-semibold text-center mt-1">{bk.date} · {f.start_time}{f.end_time ? `–${f.end_time}` : ""}</div>}
        {f.price && <div className="text-sm text-gray-500 text-center">{f.price}</div>}
        <p className="text-sm text-gray-600 mt-2 text-center">{f.description}</p>
      </div>
      <button data-testid="flash-submit" className="btn-primary w-full">{submitLabel}</button>
    </form>
  );
}

export function FlashNew() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [bookings, setBookings] = useState(null);
  useEffect(() => {
    if (loading) return;
    if (!user) { toast.info("Accedi come artista per creare un Flash Day"); nav("/accedi"); return; }
    api.get("/my/flash-eligible-bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  }, [user, loading, nav]);

  const submit = async (f) => {
    if (!f.booking_id) return toast.error("Seleziona una tua giornata MAS");
    try {
      await api.post("/flash-days", f);
      toast.success("Flash Day inviato a MAS per approvazione.");
      nav("/profilo");
    } catch (er) { toast.error(apiErr(er)); }
  };

  if (bookings === null) return <Page><div className="text-center text-gray-400 pt-20">Caricamento…</div></Page>;
  return (
    <Page>
      <SectionTitle sub="Puoi creare il tuo Flash Day per qualsiasi tua giornata MAS già prenotata.">Crea il tuo Flash Day</SectionTitle>
      <div className="card p-4 mb-4 text-sm text-gray-600" data-testid="flash-create-info">
        Se non hai ancora una prenotazione, puoi acquistare una giornata singola oppure utilizzare una giornata di un pacchetto già acquistato. Prenota la data che preferisci e potrai creare il tuo Flash Day per quella giornata.
      </div>
      {bookings.length === 0 ? (
        <div className="card p-5 text-center" data-testid="flash-no-bookings">
          <p className="text-sm text-gray-600">Non hai ancora giornate future prenotate.</p>
          <button data-testid="flash-go-book" onClick={() => nav("/prenota")} className="btn-primary mt-4">Prenota una giornata</button>
        </div>
      ) : (
        <FlashForm initial={{ ...emptyForm, phone: user?.phone || "" }} bookings={bookings} onSubmit={submit} submitLabel="Invia a MAS per approvazione" />
      )}
    </Page>
  );
}

export function FlashEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [fd, setFd] = useState(null);
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/accedi"); return; }
    api.get("/my/flash-days").then((r) => {
      const found = r.data.find((x) => x.id === id);
      if (!found) { toast.error("Flash Day non trovato"); nav("/profilo"); return; }
      setFd(found);
    }).catch(() => nav("/profilo"));
  }, [id, user, loading, nav]);

  const submit = async (f) => {
    try { await api.put(`/flash-days/${id}`, f); toast.success("Flash Day inviato nuovamente a MAS per approvazione."); nav("/profilo"); }
    catch (er) { toast.error(apiErr(er)); }
  };
  if (!fd) return <Page><div className="text-center text-gray-400 pt-20">Caricamento…</div></Page>;
  const note = fd.status === "changes_requested" ? `MAS richiede una modifica: ${fd.admin_note || ""}`
    : fd.status === "needs_update" ? "La giornata MAS associata è stata spostata. Aggiorna e reinvia." : "";
  return (
    <Page>
      <SectionTitle sub="Aggiorna e reinvia il tuo Flash Day.">Modifica Flash Day</SectionTitle>
      <FlashForm
        initial={{ booking_id: fd.booking_id, title: fd.title, start_time: fd.start_time || "18:00", end_time: fd.end_time || "", description: fd.description, price: fd.price || "", social: fd.social || "", phone: fd.phone || "", image: fd.image || "" }}
        selectedBooking={{ booking_id: fd.booking_id, date: fd.date, station_type: fd.station_type }}
        onSubmit={submit} submitLabel="Reinvia a MAS per approvazione" note={note} />
    </Page>
  );
}

export function FlashDayList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/flash-days").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <Page>
      <div className="text-center mb-1"><Logo className="inline-block" sub /></div>
      <div className="text-[0.65rem] tracking-widest uppercase text-gray-400 text-center mb-5">Tricopigmentazione · Coworking Studio · Rivoli (TO)</div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="serif text-2xl">Flash Day</h1>
        {user && user.role === "artist" && <button data-testid="flash-create-cta" onClick={() => nav("/flash/new")} className="btn-primary text-sm py-2 px-4">Crea Flash Day</button>}
      </div>
      {user && user.role === "artist" && <p className="text-sm text-gray-500 -mt-2 mb-4" data-testid="flash-create-subtext">Puoi creare il tuo Flash Day su qualsiasi tua giornata MAS già prenotata, singola o utilizzata da un pacchetto.</p>}
      {items.length === 0 && <p className="text-sm text-gray-400 text-center pt-8" data-testid="flash-empty">Nessun Flash Day pubblicato al momento.</p>}
      <div className="space-y-4">
        {items.map((fd) => (
          <button key={fd.id} data-testid={`flash-card-${fd.id}`} onClick={() => nav(`/flash/${fd.id}`)} className="card p-0 overflow-hidden text-left w-full block">
            {fd.image && <img src={imgSrc(fd.image)} alt="" className="w-full h-40 object-cover" />}
            <div className="p-4">
              <div className="serif text-lg">{fd.title}</div>
              <div className="text-sm text-gray-600">{fd.artist_name}</div>
              <div className="text-violet-700 font-semibold text-sm mt-1">{fmtDate(fd.date)} · {fd.start_time}{fd.end_time ? `–${fd.end_time}` : ""}</div>
            </div>
          </button>
        ))}
      </div>
    </Page>
  );
}

export function FlashDetail() {
  const { id } = useParams();
  const [fd, setFd] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ref = p.get("ref") || "";
    api.get(`/flash-days/${id}${ref ? `?ref=${ref}` : ""}`).then((r) => setFd(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const share = async () => {
    const url = `/api/flash-days/${id}/share`;
    const res = await shareLink({ title: `MAS · ${fd.title}`, text: `Flash Day di ${fd.artist_name} · ${fd.date}`, url });
    if (res === "copied") toast.success("Link copiato");
  };
  const send = async (e) => {
    e.preventDefault();
    try { await api.post(`/flash-days/${id}/lead`, form); toast.success("Richiesta inviata all'artista"); setForm({ name: "", phone: "" }); }
    catch (er) { toast.error(apiErr(er)); }
  };
  if (notFound) return <Page><div className="text-center text-gray-400 pt-20" data-testid="flash-notfound">Flash Day non disponibile.</div></Page>;
  if (!fd) return <Page><div className="text-center text-gray-400 pt-20">Caricamento…</div></Page>;
  const waDigits = (fd.phone || "").replace(/[^\d]/g, "");
  const telNum = (fd.phone || "").replace(/[^\d+]/g, "");
  const waText = encodeURIComponent(`Ciao, ti contatto per il Flash Day '${fd.title}' del ${fmtDate(fd.date)}.`);
  return (
    <Page>
      <div className="card p-0 overflow-hidden text-center" data-testid="flash-detail">
        {fd.image && <img src={imgSrc(fd.image)} alt="" className="w-full max-h-72 object-cover" />}
        <div className="p-6">
          <Logo className="inline-block" sub />
          <div className="text-[0.65rem] tracking-widest uppercase text-gray-400 mt-2">Tricopigmentazione · Coworking Studio · Corso Francia 162 · Rivoli (TO)</div>
          <div className="serif text-2xl mt-4">{fd.title}</div>
          <div className="text-gray-600 mt-1">Flash Day di {fd.artist_name}</div>
          <div className="text-violet-700 font-semibold mt-2">{fmtDate(fd.date)} · {fd.start_time}{fd.end_time ? `–${fd.end_time}` : ""}</div>
          {fd.price && <div className="text-sm text-gray-500 mt-1">{fd.price}</div>}
          <p className="text-sm text-gray-600 mt-3">{fd.description}</p>
          {fd.social && <a data-testid="flash-social-link" href={socialUrl(fd.social)} target="_blank" rel="noreferrer" className="btn-outline w-full mt-4 inline-flex items-center justify-center gap-2"><Instagram size={16} /> Segui / Contatta {fd.artist_name}</a>}
          {waDigits.length >= 8 && <a data-testid="flash-cta-whatsapp" href={`https://wa.me/${waDigits}?text=${waText}`} target="_blank" rel="noreferrer" className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-2"><MessageCircle size={16} /> WhatsApp</a>}
          {waDigits.length >= 8 && <a data-testid="flash-cta-call" href={`tel:${telNum}`} className="btn-outline w-full mt-3 inline-flex items-center justify-center gap-2"><Phone size={16} /> Chiama</a>}
          <button data-testid="flash-detail-share" onClick={share} className="btn-outline w-full mt-3 inline-flex items-center justify-center gap-2"><Share2 size={16} /> Condividi il Flash Day</button>
        </div>
      </div>
      <form onSubmit={send} className="card p-5 mt-5 space-y-3">
        <div className="font-semibold">Richiedi informazioni</div>
        <input data-testid="fd-lead-name" required placeholder="Il tuo nome" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="fd-lead-phone" placeholder="Telefono (facoltativo)" className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <button data-testid="fd-lead-send" className="btn-primary w-full">Contatta l'artista</button>
      </form>
    </Page>
  );
}

// ---- I miei Flash Day (Area Personale) ----
export function MyFlashDays() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  useEffect(() => { api.get("/my/flash-days").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (items.length === 0) return null;
  const share = async (fd) => {
    const url = `/api/flash-days/${fd.id}/share`;
    const res = await shareLink({ title: `MAS · ${fd.title}`, url });
    if (res === "copied") toast.success("Link copiato");
  };
  const del = async (fd) => {
    try {
      await api.delete(`/flash-days/${fd.id}`);
      toast.success("Flash Day eliminato. La tua prenotazione coworking rimane confermata.");
      setItems((prev) => prev.map((x) => x.id === fd.id ? { ...x, status: "cancelled", status_label: "Non pubblicato" } : x));
    } catch (er) { toast.error(apiErr(er)); }
    setConfirmDel(null);
  };
  return (
    <div className="card p-5 mb-4" data-testid="my-flash-days">
      <div className="font-semibold mb-3">I miei Flash Day</div>
      <div className="space-y-3">
        {items.map((fd) => {
          const editable = ["changes_requested", "needs_update", "rejected", "pending_approval"].includes(fd.status);
          return (
            <div key={fd.id} className="rounded-xl border border-gray-200 p-3" data-testid={`my-flash-${fd.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{fd.title}</div>
                  <div className="text-xs text-gray-500">{fmtDate(fd.date)} · {TYPE_LABEL[fd.station_type]}</div>
                </div>
                <span className="badge-chip state-ATTESA" data-testid={`my-flash-status-${fd.id}`}>{fd.status_label}</span>
              </div>
              {fd.status === "cancelled" && <div className="text-xs text-red-600 mt-1" data-testid={`my-flash-cancel-msg-${fd.id}`}>{fd.cancel_reason === "artist_cancelled" ? "Flash Day eliminato. La prenotazione coworking associata rimane confermata." : "Flash Day non più pubblicato perché la giornata MAS associata è stata cancellata."}</div>}
              {fd.status === "changes_requested" && fd.admin_note && <div className="text-xs text-amber-700 mt-1">MAS: {fd.admin_note}</div>}
              <div className="flex gap-2 mt-2 flex-wrap">
                {editable && <button data-testid={`my-flash-edit-${fd.id}`} onClick={() => nav(`/flash/${fd.id}/edit`)} className="btn-outline text-xs py-1.5 px-3">{fd.status === "pending_approval" ? "Modifica" : "Aggiorna e reinvia"}</button>}
                {fd.status === "approved" && <>
                  <button onClick={() => nav(`/flash/${fd.id}`)} className="btn-outline text-xs py-1.5 px-3">Visualizza</button>
                  <button onClick={() => share(fd)} className="btn-outline text-xs py-1.5 px-3">Condividi</button>
                </>}
                {fd.status !== "cancelled" && <button data-testid={`my-flash-delete-${fd.id}`} onClick={() => setConfirmDel(fd)} className="btn-outline text-xs py-1.5 px-3 text-red-600 border-red-200">Elimina Flash Day</button>}
              </div>
            </div>
          );
        })}
      </div>
      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[60]" data-testid="flash-delete-confirm" onClick={() => setConfirmDel(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold mb-2">Eliminare questo Flash Day?</div>
            <p className="text-sm text-gray-600">Il Flash Day non sarà più visibile al pubblico. La tua prenotazione coworking rimarrà invariata.</p>
            <div className="flex gap-2 mt-4">
              <button data-testid="flash-delete-cancel" onClick={() => setConfirmDel(null)} className="btn-outline flex-1 text-sm">Annulla</button>
              <button data-testid="flash-delete-confirm-btn" onClick={() => del(confirmDel)} className="btn-primary flex-1 text-sm">Elimina Flash Day</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ================= FUNZIONI PRESERVATE (Vetrina, ProfileComplete) =================
const SLOT_TIMES = [];
for (let h = 10; h <= 20; h++) { for (let m = 0; m < 60; m += 30) { SLOT_TIMES.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`); } }
const MAX_CHARS = 70;

export function FlashLed() {
  const nav = useNavigate();
  const { user } = useAuth();
  const minDate = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({ name: "", phone: "", recipient_name: "", message: "", date: minDate, time: "18:00" });
  const [taken, setTaken] = useState([]);
  const [cfg, setCfg] = useState({ price: 0 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/vetrina/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (f.date) api.get(`/vetrina/slots?date=${f.date}`).then((r) => setTaken(r.data.taken)).catch(() => {}); }, [f.date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Accedi per pubblicare un messaggio in Vetrina"); nav("/accedi"); return; }
    if (!f.name || !f.phone || !f.message) return toast.error("Compila nome, cellulare e messaggio");
    if (f.message.length > MAX_CHARS) return toast.error(`Massimo ${MAX_CHARS} caratteri`);
    setBusy(true);
    try {
      const { data } = await api.post("/vetrina/message", f);
      if (data.requires_payment) {
        const { data: chk } = await api.post(`/vetrina/${data.id}/checkout`, { origin_url: checkoutOriginUrl(), native: isNative() });
        goToCheckout(chk.checkout_url);
        return;
      }
      toast.success("Richiesta inviata! MAS la verificherà a breve.");
      nav(`/vetrina/success?date=${f.date}&time=${f.time}`);
    } catch (er) { toast.error(apiErr(er)); setBusy(false); }
  };

  const remaining = MAX_CHARS - f.message.length;
  const readable = f.message.length <= MAX_CHARS && f.message.length > 0;

  return (
    <Page>
      <div className="text-center mb-2"><h2 className="serif text-2xl">Sorprendi qualcuno.</h2></div>
      <p className="text-sm text-gray-500 text-center mb-5">Scrivi il tuo messaggio e fallo comparire sulla vetrina MAS per 30 minuti.</p>
      <div className="mx-auto mb-5" style={{ maxWidth: 220 }}>
        <div className="bg-black rounded-xl p-4 flex flex-col items-center justify-center text-center" style={{ aspectRatio: "1 / 1.5" }} data-testid="vetrina-preview">
          <div className="text-white font-semibold leading-snug break-words" style={{ fontSize: f.message.length > 40 ? "1rem" : "1.35rem" }}>
            {f.message || "Il tuo messaggio…"}
          </div>
        </div>
        <div className={`text-center text-xs mt-2 ${remaining < 0 ? "text-red-500" : "text-gray-400"}`} data-testid="vetrina-counter">{f.message.length}/{MAX_CHARS} caratteri</div>
        {!readable && f.message.length > 0 && <div className="text-center text-xs text-amber-600">Testo troppo lungo per una buona leggibilità.</div>}
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="vetrina-name" required placeholder="Il tuo nome" className={inp} value={f.name} onChange={set("name")} />
        <div>
          <input data-testid="vetrina-phone" required placeholder="Il tuo numero di cellulare" className={inp} value={f.phone} onChange={set("phone")} />
          <span className="block text-xs text-gray-400 mt-1">È il numero di chi organizza la sorpresa. Non chiediamo il numero del destinatario.</span>
        </div>
        <input data-testid="vetrina-recipient" placeholder="Nome della persona a cui è dedicato" className={inp} value={f.recipient_name} onChange={set("recipient_name")} />
        <textarea data-testid="vetrina-message" required maxLength={MAX_CHARS} placeholder="Scrivi il tuo messaggio (max 70 caratteri)" className={inp} value={f.message} onChange={set("message")} />
        <label className="block text-sm">Scegli il giorno
          <input data-testid="vetrina-date" type="date" required min={minDate} className={inp} value={f.date} onChange={set("date")} />
          <span className="block text-xs text-gray-400 mt-1">Prenota con almeno 3 giorni di anticipo.</span>
        </label>
        <label className="block text-sm">Scegli l'orario (fasce da 30 minuti)
          <select data-testid="vetrina-time" className={inp} value={f.time} onChange={set("time")}>
            {SLOT_TIMES.map((t) => <option key={t} value={t} disabled={taken.includes(t)}>{t}{taken.includes(t) ? " — occupato" : ""}</option>)}
          </select>
        </label>
        <div className="text-sm text-gray-600" data-testid="vetrina-price-label">{cfg.price > 0 ? `Servizio a pagamento: ${cfg.price.toFixed(2)} € per 30 minuti` : "Promo lancio: gratuito"}</div>
        <button data-testid="vetrina-submit" disabled={busy} className="btn-primary w-full">{busy ? "Attendere…" : (cfg.price > 0 ? `Pubblica in Vetrina — ${cfg.price.toFixed(2)} €` : "Pubblica in Vetrina")}</button>
        <p className="text-xs text-gray-400 text-center">{cfg.price > 0 ? "Il messaggio sarà pubblicato solo dopo il pagamento e la verifica di MAS." : "La richiesta sarà verificata e approvata da MAS prima della trasmissione."}</p>
      </form>
    </Page>
  );
}

export function VetrinaSuccess() {
  const nav = useNavigate();
  const p = new URLSearchParams(window.location.search);
  const date = p.get("date"); const time = p.get("time");
  const cal = () => {
    const dt = (date || "").replace(/-/g, "") + "T" + (time || "18:00").replace(":", "") + "00";
    const end = (() => { const [h, m] = (time || "18:00").split(":").map(Number); const e = new Date(2000, 0, 1, h, m + 30); return `${String(e.getHours()).padStart(2, "0")}${String(e.getMinutes()).padStart(2, "0")}00`; })();
    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:Messaggio in vetrina MAS\nLOCATION:Corso Francia 162, Rivoli (TO)\nDTSTART:${dt}\nDTEND:${(date || "").replace(/-/g, "")}T${end}\nDESCRIPTION:Il tuo messaggio sulla vetrina MAS\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([ics], { type: "text/calendar" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "mas-vetrina.ics"; a.click();
  };
  return (
    <Page>
      <div className="text-center pt-14">
        <Logo className="inline-block" sub />
        <h1 className="serif text-2xl mt-6">Richiesta inviata!</h1>
        <p className="text-gray-600 mt-2">Il tuo messaggio sarà visibile il {date} dalle {time}. Ti confermeremo su WhatsApp dopo l'approvazione.</p>
        <button data-testid="vetrina-cal" onClick={cal} className="btn-primary mt-6">Aggiungi al calendario</button>
        <button data-testid="vetrina-home" onClick={() => nav("/")} className="btn-outline mt-3">Torna alla Home</button>
      </div>
    </Page>
  );
}

export function ProfileComplete() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ surname: "", artist_name: "", technique: "", instagram: "", city: "", bio: "", publish_consent: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/my/profile", { ...f, portfolio: [] }); toast.success(`Profilo ${data.completion}% completo. In attesa di approvazione.`); refresh(); nav("/profilo"); }
    catch (er) { toast.error(apiErr(er)); }
  };
  return (
    <Page>
      <SectionTitle sub="Completa il tuo profilo per comparire nella sezione Artisti.">Completa profilo</SectionTitle>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">Nome artistico<input data-testid="pc-artistname" className={inp} value={f.artist_name} onChange={set("artist_name")} /></label>
        <label className="block text-sm">Tecnica<input data-testid="pc-technique" className={inp} value={f.technique} onChange={set("technique")} /></label>
        <label className="block text-sm">Instagram<input data-testid="pc-instagram" className={inp} value={f.instagram} onChange={set("instagram")} /></label>
        <label className="block text-sm">Città<input data-testid="pc-city" className={inp} value={f.city} onChange={set("city")} /></label>
        <label className="block text-sm">Bio breve<textarea data-testid="pc-bio" className={inp} value={f.bio} onChange={set("bio")} /></label>
        <label className="flex items-start gap-2 text-xs text-gray-600 pt-1">
          <input data-testid="pc-consent" type="checkbox" className="mt-0.5" checked={f.publish_consent} onChange={set("publish_consent")} />
          Acconsento alla pubblicazione del mio profilo nella sezione Artisti.
        </label>
        <button data-testid="pc-submit" className="btn-primary w-full">Salva profilo</button>
      </form>
    </Page>
  );
}
